import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import {defaultUser} from "../generic/InitialStates";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {AGCCheckBox} from "../../generic-components/AGCCheckBox";
import {AGCText} from "../../generic-components/AGCText";
import {AGCStaticSelect} from "../../generic-components/AGCStaticSelect";
import {apiGenericCreate, apiGenericUpdate, genericGetOne} from "../generic/crud";
import {ENTITY_ID_DESIGNATION, ENTITY_ID_ROLE, ENTITY_ID_USER} from "../generic/Constants";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {VALIDATE_ONLY_SMALL_CHARS} from "../../generic-components/Validations";

export function UserAddEditDialog(props) {
    const {show, handleClose, id, onSave} = props;
    const [objectForSubmit, setObjectForSubmit] = useState(defaultUser);
    const [isLoading, setIsLoading] = useState(false);
    const [forUpdate, setForUpdate] = useState(false);
    const [title, setTitle] = useState("User");

    const entityName = "User";

    // Dashboard types data for static select
    const dashboardTypes = [
        {id: "NO_DASHBOARD", name: "No Dashboard"},
        {id: "ADMIN_DASHBOARD", name: "Admin Dashboard"},
        {id: "FINANCE_DASHBOARD", name: "Finance Dashboard"},
        {id: "MANAGER_DASHBOARD", name: "Manager Dashboard"},
        {id: "SUPERVISOR_DASHBOARD", name: "Supervisor Dashboard"}
    ];

    const formik = useFormik({
        initialValues: objectForSubmit,
        enableReinitialize: true,
        onSubmit(user, {resetForm}) {
            setIsLoading(true);
            user.designation = user.designation.id;
            user.role = user.role.id;
            user.dashboardType = user.dashboardType.id;

            if (!id) {
                apiGenericCreate(ENTITY_ID_USER, user).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch(() => {
                    setIsLoading(false);
                });
            } else {
                apiGenericUpdate(ENTITY_ID_USER, user, id).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch(() => {
                    setIsLoading(false);
                    objectForSubmit.role = {
                        id: objectForSubmit.role,
                        name: objectForSubmit.roleName
                    };
                    objectForSubmit.designation = {
                        id: objectForSubmit.designation,
                        name: objectForSubmit.designationName
                    };
                    // Restore dashboardType object from string
                    const dashboardTypeObj = dashboardTypes.find(dt => dt.id === objectForSubmit.dashboardType);
                    if (dashboardTypeObj) {
                        objectForSubmit.dashboardType = dashboardTypeObj;
                    }
                    setObjectForSubmit(objectForSubmit);
                });
            }
        }
    });

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit " + entityName);
            setIsLoading(true);
            genericGetOne(ENTITY_ID_USER, id).then(
                ({data}) => {
                    data.role = {id: data.role, name: data.roleName};
                    data.designation = {id: data.designation, name: data.designationName};

                    // Convert dashboardType string to object for AGCStaticSelect
                    const dashboardTypeObj = dashboardTypes.find(dt => dt.id === data.dashboardType);
                    if (dashboardTypeObj) {
                        data.dashboardType = dashboardTypeObj;
                    } else {
                        // Default to Admin Dashboard if not found
                        data.dashboardType = dashboardTypes[0];
                    }

                    setObjectForSubmit(data);
                    setTitle("Update " + entityName);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        } else {
            setForUpdate(false);
            setObjectForSubmit(defaultUser);
            setTitle("New " + entityName);
        }
    }, [show]);

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading && (
                    <Form className="form form-label-right">
                        <div className="form-group row">

                            <Form.Group className="col-lg-4">
                                <AGCText formik={formik}
                                         validation={[VALIDATE_ONLY_SMALL_CHARS]}
                                         name={"User Name"}
                                         placeHolder={'Enter Login UserName'}
                                         id={"id"}
                                         toolTip={"Please enter the Login UserName"}
                                         readOnly={forUpdate}
                                         value={formik.values.id}/>
                            </Form.Group>

                            <Form.Group className="col-lg-4">
                                <AGCText formik={formik}
                                         name={"Full Name"}
                                         placeHolder={'Enter Full Name'}
                                         id={"name"}
                                         toolTip={"Please enter Full Name"}
                                         value={formik.values.name}/>
                            </Form.Group>

                            <Form.Group className="col-lg-4">
                                <AGCText formik={formik}
                                         name={"Phone"}
                                         placeHolder={'Enter Phone Number'}
                                         id={"phone"}
                                         toolTip={"Please enter a valid Phone Number"}
                                         value={formik.values.phone}/>
                            </Form.Group>

                        </div>

                        <br/>
                        <br/>
                        <div className="form-group row">
                            <Form.Group className="col-lg-4">
                                <AGCSelect
                                    formik={formik}
                                    isMulti={false}
                                    id={'designation'}
                                    name={"Select Designation"}
                                    entityId={ENTITY_ID_DESIGNATION}
                                    value={formik.values.designation}
                                    toolTip={"Please Select a Designation"}/>
                            </Form.Group>

                            <Form.Group className="col-lg-4">
                                <AGCSelect
                                    formik={formik}
                                    isMulti={false}
                                    id={'role'}
                                    name={"Select Role"}
                                    entityId={ENTITY_ID_ROLE}
                                    value={formik.values.role}
                                    toolTip={"Please Select a Role, Roles will be use to provide permissions."}/>
                            </Form.Group>

                            <Form.Group className="col-lg-4">
                                <AGCStaticSelect
                                    formik={formik}
                                    isMulti={false}
                                    id={'dashboardType'}
                                    name={"Dashboard Type"}
                                    data={dashboardTypes}
                                    value={formik.values.dashboardType}
                                    toolTip={"Select the dashboard view for this user"}
                                    required={true}
                                    includeAllOption={false}
                                />
                            </Form.Group>
                        </div>

                        <br/>
                        <div className="form-group row">
                            <Form.Group className="col-lg-4">
                                <AGCCheckBox formik={formik}
                                             name={"Active"}
                                             id={"active"}
                                             toolTip={"Activate / Inactivate User Login"}
                                             value={formik.values.active}/>
                            </Form.Group>
                        </div>

                        <div className="p-10 text-center">
                            <span className='required'/>
                            <Form.Text className="text-muted "> After the <b>User</b> is created, You will be able to
                                set the <b>Password</b> for the User, Also, if the <b>User</b> is marked
                                as <b>Inactive</b> he/she will not be able to login.</Form.Text>
                        </div>

                        <Modal.Footer>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-elevate"
                                    onClick={handleClose}>
                                    Cancel
                                </button>
                                <> </>
                                <button
                                    type="submit"
                                    onClick={formik.handleSubmit}
                                    className="btn btn-primary btn-elevate">
                                    Save
                                </button>
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
                {isLoading && <span>Please Wait, Fetching Information from Server</span>}
            </Modal.Body>
        </Modal>
    );
}
